import type { Customer, EstimateRules, Maker, Pattern, Process, Product, Proposal, Revision } from "@el/schema";
import { useLiveQuery } from "dexie-react-hooks";
import { useMemo } from "react";
import { getLocal } from "./db";

/** 端末のデータの読み込み状態。取得中に 0 件を出さないため、読み込み中と失敗を分ける */
export type Loaded<T> = { status: "loading" } | { status: "error"; message: string } | { status: "ready"; data: T };

const READ_ERROR = "端末のデータを読み込めませんでした。画面を読み込み直してください。";

function useLocal<T>(query: () => Promise<T>, deps: unknown[]): Loaded<T> {
  const res = useLiveQuery(async () => {
    try {
      return { ok: true as const, data: await query() };
    } catch {
      return { ok: false as const };
    }
  }, deps);
  return useMemo<Loaded<T>>(() => {
    if (res === undefined) return { status: "loading" };
    if (!res.ok) return { status: "error", message: READ_ERROR };
    return { status: "ready", data: res.data };
  }, [res]);
}

const db = () => getLocal().db;

export const byUpdatedDesc = <T extends { updatedAt: string }>(a: T, b: T) => (a.updatedAt < b.updatedAt ? 1 : a.updatedAt > b.updatedAt ? -1 : 0);

export function useProposals(): Loaded<Proposal[]> {
  return useLocal(async () => (await db().proposals.toArray()).filter((p) => !p.deletedAt).sort(byUpdatedDesc), []);
}
export function useProposal(id: string | undefined): Loaded<Proposal | null> {
  return useLocal(async () => (id ? ((await db().proposals.get(id)) ?? null) : null), [id]);
}
export function useRevisions(proposalId: string | undefined): Loaded<Revision[]> {
  return useLocal(async () => (proposalId ? (await db().revisions.where("proposalId").equals(proposalId).toArray()).sort((a, b) => (a.createdAt < b.createdAt ? -1 : 1)) : []), [proposalId]);
}
export function useRevision(id: string | undefined): Loaded<Revision | null> {
  return useLocal(async () => (id ? ((await db().revisions.get(id)) ?? null) : null), [id]);
}
/** 一覧に出す、提案ごとの現在の版（無ければ最後の版） */
export function useCurrentRevisions(): Loaded<Map<string, Revision>> {
  return useLocal(async () => {
    const [props, revs] = await Promise.all([db().proposals.toArray(), db().revisions.toArray()]);
    const byId = new Map(revs.map((r) => [r.id, r]));
    const latest = new Map<string, Revision>();
    for (const r of revs) {
      const cur = latest.get(r.proposalId);
      if (!cur || cur.createdAt < r.createdAt) latest.set(r.proposalId, r);
    }
    const out = new Map<string, Revision>();
    for (const p of props) {
      const r = (p.currentRevisionId && byId.get(p.currentRevisionId)) || latest.get(p.id);
      if (r) out.set(p.id, r);
    }
    return out;
  }, []);
}
export function useCustomers(): Loaded<Customer[]> {
  return useLocal(async () => (await db().customers.toArray()).filter((c) => !c.deletedAt).sort((a, b) => a.name.localeCompare(b.name, "ja")), []);
}
export function useCustomer(id: string | undefined): Loaded<Customer | null> {
  return useLocal(async () => (id ? ((await db().customers.get(id)) ?? null) : null), [id]);
}
export function useProcesses(): Loaded<Process[]> {
  return useLocal(async () => (await db().processes.toArray()).sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, "ja")), []);
}
export function useProducts(): Loaded<Product[]> {
  return useLocal(async () => (await db().products.toArray()).sort((a, b) => a.code.localeCompare(b.code, "ja")), []);
}
export function useMakers(): Loaded<Maker[]> {
  return useLocal(async () => (await db().makers.toArray()).sort((a, b) => a.name.localeCompare(b.name, "ja")), []);
}
export function usePatterns(): Loaded<Pattern[]> {
  return useLocal(async () => (await db().patterns.toArray()).sort((a, b) => a.name.localeCompare(b.name, "ja")), []);
}
/** 既定の見積ルール（isDefault、無ければ最後に更新されたもの） */
export function useDefaultRules(): Loaded<EstimateRules | null> {
  return useLocal(async () => pickDefaultRules(await db().estimateRules.toArray()), []);
}
export function useRulesById(): Loaded<Map<string, EstimateRules>> {
  return useLocal(async () => new Map((await db().estimateRules.toArray()).map((r) => [r.id, r])), []);
}
export function useOutboxErrors() {
  return useLocal(() => getLocal().store.outboxErrors(), []);
}
export function useOutboxCount(): Loaded<number> {
  return useLocal(() => getLocal().db.outbox.count(), []);
}

export function pickDefaultRules(rules: EstimateRules[]): EstimateRules | null {
  if (!rules.length) return null;
  const defaults = rules.filter((r) => r.isDefault).sort(byUpdatedDesc);
  return defaults[0] ?? [...rules].sort(byUpdatedDesc)[0] ?? null;
}

/** 複数の読み込みをまとめる（全部そろってから出す。R11） */
export function all<T extends readonly Loaded<unknown>[]>(...xs: T): Loaded<{ [K in keyof T]: T[K] extends Loaded<infer U> ? U : never }> {
  for (const x of xs) if (x.status === "error") return x;
  for (const x of xs) if (x.status === "loading") return { status: "loading" };
  return { status: "ready", data: xs.map((x) => (x as { data: unknown }).data) as never };
}
